import {
  Card,
  Group,
  Text,
  Switch,
  Badge,
  Stack,
  ThemeIcon,
} from "@mantine/core";
import { FEATURE_FLAGS, FEATURE_FLAGS_METADATA } from "@congrs/schemas";
import { useUpdateFeatureFlag } from "../../../../api/featureFlags";
import { useState } from "react";
import dayjs from "dayjs";
import ConfirmationModal from "./ConfirmationModal";
import { getFeatureStyle } from "../../featureStyles";

export default function FeatureCard({ feature }) {
  const metadata = FEATURE_FLAGS_METADATA[feature.featureName];
  const style = getFeatureStyle(feature.featureName);
  const Icon = style.icon;
  const color = style.color;

  const [modalOpened, setModalOpened] = useState(false);
  const [newEnabled, setNewEnabled] = useState(feature.enabled);

  const updateMutation = useUpdateFeatureFlag(feature.id);

  // Turning the compliance gate on mid-event stops every attendee at the terms
  // the next time they enter the event, including people already deep in using
  // the app. That is intended, but disruptive enough to say out loud.
  const warning =
    feature.featureName === FEATURE_FLAGS.EVENT_COMPLIANCE && newEnabled
      ? "Every attendee will be asked to accept the compliance text the next time they enter this event, including anyone using the app right now."
      : null;

  const handleSwitchClick = () => {
    setNewEnabled(!feature.enabled);
    setModalOpened(true);
  };

  const handleConfirm = () => {
    setModalOpened(false);
    updateMutation.mutate({ enabled: newEnabled });
  };

  return (
    <>
      <Card shadow="sm" radius="md" padding="lg" withBorder>
        <Group justify="space-between" align="flex-start" mb="md">
          <Group gap="md" wrap="nowrap">
            <ThemeIcon
              size="xl"
              radius="md"
              variant={feature.enabled ? "filled" : "light"}
              color={color}
            >
              <Icon size={22} />
            </ThemeIcon>
            <Stack gap={4}>
              <Text fw={600} size="md">
                {metadata?.label}
              </Text>
              <Text size="sm" c="dimmed" lh={1.4}>
                {metadata?.description}
              </Text>
            </Stack>
          </Group>

          <Badge
            variant="light"
            color={feature.enabled ? "green" : "gray"}
            size="lg"
            style={{ flexShrink: 0 }}
          >
            {feature.enabled ? "Active" : "Inactive"}
          </Badge>
        </Group>

        <Group justify="space-between" align="center">
          <Text size="xs" c="dimmed">
            {feature.enabledBy
              ? `Last changed by ${feature.enabledBy}`
              : "Not yet modified"}
            {feature.updatedAt
              ? ` · ${dayjs(feature.updatedAt).format("YYYY-MM-DD HH:mm:ss")}`
              : ""}
          </Text>

          <Switch
            size="md"
            checked={feature.enabled}
            onChange={handleSwitchClick}
            onLabel="ON"
            offLabel="OFF"
            color={color}
          />
        </Group>
      </Card>

      <ConfirmationModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        onConfirm={handleConfirm}
        pendingToggle={feature.enabled}
        warning={warning}
      />
    </>
  );
}
